import type {
	API_CreateInvoiceRequest,
	API_CreateOfflinePaymentRequest,
	API_CreateProviderAccountRequest,
	API_CreateRefundRequest,
	API_GetInvoiceResponse,
	API_GetPaymentResponse,
	API_IssueInvoiceRequest,
	API_ListInvoicesResponse,
	API_ListPaymentsResponse,
	API_ListProviderAccountsResponse,
	API_ListRefundsResponse,
	API_UpdateInvoiceRequest,
	API_UpdateProviderAccountRequest,
	Invoice,
	PaginationRequest,
	Payment,
	PaymentProviderAccount,
	PaymentRefund,
} from "@pilatestime/shared-types";
import { queryOptions, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useMutationWithToast } from "@/hooks/useMutationWithToast";
import api from "./api_client";

// ============================================
// INVOICE FILTER PARAMS
// ============================================

export interface InvoiceFilterParams extends PaginationRequest {
	status?: string;
	customerId?: string;
	enrollmentId?: string;
	search?: string;
}

export interface PaymentFilterParams extends PaginationRequest {
	status?: string;
	customerId?: string;
	invoiceId?: string;
	search?: string;
}

// ============================================
// API FUNCTIONS - PROVIDER ACCOUNTS
// ============================================

export const fetchProviderAccounts = (
	token: string,
	organizationId: string,
): Promise<API_ListProviderAccountsResponse> =>
	api
		.get(`/organizations/${organizationId}/payment-providers`, {
			headers: { Authorization: `Bearer ${token}` },
		})
		.then((res) => res.data);

export const fetchProviderAccountById = (
	token: string,
	organizationId: string,
	accountId: string,
): Promise<PaymentProviderAccount> =>
	api
		.get(`/organizations/${organizationId}/payment-providers/${accountId}`, {
			headers: { Authorization: `Bearer ${token}` },
		})
		.then((res) => res.data);

export const createProviderAccount = (
	token: string,
	organizationId: string,
	data: API_CreateProviderAccountRequest,
): Promise<PaymentProviderAccount> =>
	api
		.post(`/organizations/${organizationId}/payment-providers`, data, {
			headers: { Authorization: `Bearer ${token}` },
		})
		.then((res) => res.data);

export const updateProviderAccount = (
	token: string,
	organizationId: string,
	accountId: string,
	data: API_UpdateProviderAccountRequest,
): Promise<PaymentProviderAccount> =>
	api
		.put(
			`/organizations/${organizationId}/payment-providers/${accountId}`,
			data,
			{
				headers: { Authorization: `Bearer ${token}` },
			},
		)
		.then((res) => res.data);

export const deleteProviderAccount = (
	token: string,
	organizationId: string,
	accountId: string,
): Promise<void> =>
	api
		.delete(`/organizations/${organizationId}/payment-providers/${accountId}`, {
			headers: { Authorization: `Bearer ${token}` },
		})
		.then(() => undefined);

// ============================================
// API FUNCTIONS - INVOICES
// ============================================

export const fetchInvoices = (
	token: string,
	organizationId: string,
	params?: InvoiceFilterParams,
): Promise<API_ListInvoicesResponse> =>
	api
		.get(`/organizations/${organizationId}/invoices`, {
			headers: { Authorization: `Bearer ${token}` },
			params,
		})
		.then((res) => res.data);

export const fetchInvoiceById = (
	token: string,
	organizationId: string,
	invoiceId: string,
): Promise<API_GetInvoiceResponse> =>
	api
		.get(`/organizations/${organizationId}/invoices/${invoiceId}`, {
			headers: { Authorization: `Bearer ${token}` },
		})
		.then((res) => res.data);

export const createInvoice = (
	token: string,
	organizationId: string,
	data: API_CreateInvoiceRequest,
): Promise<Invoice> =>
	api
		.post(`/organizations/${organizationId}/invoices`, data, {
			headers: { Authorization: `Bearer ${token}` },
		})
		.then((res) => res.data);

export const updateInvoice = (
	token: string,
	organizationId: string,
	invoiceId: string,
	data: API_UpdateInvoiceRequest,
): Promise<Invoice> =>
	api
		.put(`/organizations/${organizationId}/invoices/${invoiceId}`, data, {
			headers: { Authorization: `Bearer ${token}` },
		})
		.then((res) => res.data);

export const issueInvoice = (
	token: string,
	organizationId: string,
	invoiceId: string,
	data?: API_IssueInvoiceRequest,
): Promise<Invoice> =>
	api
		.post(
			`/organizations/${organizationId}/invoices/${invoiceId}/issue`,
			data || {},
			{
				headers: { Authorization: `Bearer ${token}` },
			},
		)
		.then((res) => res.data);

export const cancelInvoice = (
	token: string,
	organizationId: string,
	invoiceId: string,
	reason?: string,
): Promise<Invoice> =>
	api
		.post(
			`/organizations/${organizationId}/invoices/${invoiceId}/cancel`,
			{ reason },
			{ headers: { Authorization: `Bearer ${token}` } },
		)
		.then((res) => res.data);

// ============================================
// API FUNCTIONS - PAYMENTS
// ============================================

export const fetchPayments = (
	token: string,
	organizationId: string,
	params?: PaymentFilterParams,
): Promise<API_ListPaymentsResponse> =>
	api
		.get(`/organizations/${organizationId}/payments`, {
			headers: { Authorization: `Bearer ${token}` },
			params,
		})
		.then((res) => res.data);

export const fetchPaymentById = (
	token: string,
	organizationId: string,
	paymentId: string,
): Promise<API_GetPaymentResponse> =>
	api
		.get(`/organizations/${organizationId}/payments/${paymentId}`, {
			headers: { Authorization: `Bearer ${token}` },
		})
		.then((res) => res.data);

export const createOfflinePayment = (
	token: string,
	organizationId: string,
	data: API_CreateOfflinePaymentRequest,
): Promise<Payment> =>
	api
		.post(`/organizations/${organizationId}/payments/offline`, data, {
			headers: { Authorization: `Bearer ${token}` },
		})
		.then((res) => res.data);

export const cancelPayment = (
	token: string,
	organizationId: string,
	paymentId: string,
	reason?: string,
): Promise<Payment> =>
	api
		.post(
			`/organizations/${organizationId}/payments/${paymentId}/cancel`,
			{ reason },
			{ headers: { Authorization: `Bearer ${token}` } },
		)
		.then((res) => res.data);

// ============================================
// API FUNCTIONS - REFUNDS
// ============================================

export const fetchRefunds = (
	token: string,
	organizationId: string,
	paymentId: string,
	params?: PaginationRequest,
): Promise<API_ListRefundsResponse> =>
	api
		.get(`/organizations/${organizationId}/payments/${paymentId}/refunds`, {
			headers: { Authorization: `Bearer ${token}` },
			params,
		})
		.then((res) => res.data);

export const createRefund = (
	token: string,
	organizationId: string,
	paymentId: string,
	data: Omit<API_CreateRefundRequest, "paymentId">,
): Promise<PaymentRefund> =>
	api
		.post(
			`/organizations/${organizationId}/payments/${paymentId}/refund`,
			data,
			{
				headers: { Authorization: `Bearer ${token}` },
			},
		)
		.then((res) => res.data);

// ============================================
// QUERY OPTIONS
// ============================================

export const fetchProviderAccountsQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
) =>
	queryOptions({
		queryKey: ["provider_accounts", organizationId, userId],
		queryFn: () => fetchProviderAccounts(token, organizationId),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

export const fetchInvoicesQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	params?: InvoiceFilterParams,
) =>
	queryOptions({
		queryKey: ["invoices", organizationId, userId, params],
		queryFn: () => fetchInvoices(token, organizationId, params),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

export const fetchInvoiceByIdQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	invoiceId: string,
) =>
	queryOptions({
		queryKey: ["invoice", organizationId, invoiceId, userId],
		queryFn: () => fetchInvoiceById(token, organizationId, invoiceId),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

export const fetchCustomerInvoicesQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	customerId: string,
	params?: Omit<InvoiceFilterParams, "customerId">,
) =>
	queryOptions({
		queryKey: ["customer_invoices", organizationId, customerId, userId, params],
		queryFn: () =>
			fetchInvoices(token, organizationId, { ...params, customerId }),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

export const fetchEnrollmentInvoicesQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	enrollmentId: string,
	params?: Omit<InvoiceFilterParams, "enrollmentId">,
) =>
	queryOptions({
		queryKey: [
			"enrollment_invoices",
			organizationId,
			enrollmentId,
			userId,
			params,
		],
		queryFn: () =>
			fetchInvoices(token, organizationId, { ...params, enrollmentId }),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

export const fetchPaymentsQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	params?: PaymentFilterParams,
) =>
	queryOptions({
		queryKey: ["payments", organizationId, userId, params],
		queryFn: () => fetchPayments(token, organizationId, params),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

export const fetchPaymentByIdQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	paymentId: string,
) =>
	queryOptions({
		queryKey: ["payment", organizationId, paymentId, userId],
		queryFn: () => fetchPaymentById(token, organizationId, paymentId),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

export const fetchCustomerPaymentsQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	customerId: string,
	params?: Omit<PaymentFilterParams, "customerId">,
) =>
	queryOptions({
		queryKey: ["customer_payments", organizationId, customerId, userId, params],
		queryFn: () =>
			fetchPayments(token, organizationId, { ...params, customerId }),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

// ============================================
// MUTATION HOOKS
// ============================================

// --- Provider Account Mutations ---

export const useCreateProviderAccount = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			data,
		}: {
			token: string;
			organizationId: string;
			data: API_CreateProviderAccountRequest;
		}) => createProviderAccount(token, organizationId, data),
		toastMessages: {
			loading: t("billing.providerAccounts.toasts.creating"),
			success: t("billing.providerAccounts.toasts.createSuccess"),
			error: t("billing.providerAccounts.toasts.createError"),
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["provider_accounts", variables.organizationId],
			});
		},
	});
};

export const useUpdateProviderAccount = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			accountId,
			data,
		}: {
			token: string;
			organizationId: string;
			accountId: string;
			data: API_UpdateProviderAccountRequest;
		}) => updateProviderAccount(token, organizationId, accountId, data),
		toastMessages: {
			loading: t("billing.providerAccounts.toasts.updating"),
			success: t("billing.providerAccounts.toasts.updateSuccess"),
			error: t("billing.providerAccounts.toasts.updateError"),
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["provider_accounts", variables.organizationId],
			});
		},
	});
};

export const useDeleteProviderAccount = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			accountId,
		}: {
			token: string;
			organizationId: string;
			accountId: string;
		}) => deleteProviderAccount(token, organizationId, accountId),
		toastMessages: {
			loading: t("billing.providerAccounts.toasts.deleting"),
			success: t("billing.providerAccounts.toasts.deleteSuccess"),
			error: t("billing.providerAccounts.toasts.deleteError"),
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["provider_accounts", variables.organizationId],
			});
		},
	});
};

// --- Invoice Mutations ---

export const useCreateInvoice = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			data,
		}: {
			token: string;
			organizationId: string;
			data: API_CreateInvoiceRequest;
		}) => createInvoice(token, organizationId, data),
		toastMessages: {
			loading: t("billing.invoices.toasts.creating"),
			success: t("billing.invoices.toasts.createSuccess"),
			error: t("billing.invoices.toasts.createError"),
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["invoices", variables.organizationId],
			});
			if (variables.data.customerId) {
				queryClient.invalidateQueries({
					queryKey: [
						"customer_invoices",
						variables.organizationId,
						variables.data.customerId,
					],
				});
			}
			if (variables.data.enrollmentId) {
				queryClient.invalidateQueries({
					queryKey: [
						"enrollment_invoices",
						variables.organizationId,
						variables.data.enrollmentId,
					],
				});
			}
		},
	});
};

export const useUpdateInvoice = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			invoiceId,
			data,
		}: {
			token: string;
			organizationId: string;
			invoiceId: string;
			data: API_UpdateInvoiceRequest;
		}) => updateInvoice(token, organizationId, invoiceId, data),
		toastMessages: {
			loading: t("billing.invoices.toasts.updating"),
			success: t("billing.invoices.toasts.updateSuccess"),
			error: t("billing.invoices.toasts.updateError"),
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["invoices", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["invoice", variables.organizationId, variables.invoiceId],
			});
		},
	});
};

export const useIssueInvoice = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			invoiceId,
			data,
		}: {
			token: string;
			organizationId: string;
			invoiceId: string;
			data?: API_IssueInvoiceRequest;
		}) => issueInvoice(token, organizationId, invoiceId, data),
		toastMessages: {
			loading: t("billing.invoices.toasts.issuing"),
			success: t("billing.invoices.toasts.issueSuccess"),
			error: t("billing.invoices.toasts.issueError"),
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["invoices", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["invoice", variables.organizationId, variables.invoiceId],
			});
		},
	});
};

export const useCancelInvoice = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			invoiceId,
			reason,
		}: {
			token: string;
			organizationId: string;
			invoiceId: string;
			reason?: string;
		}) => cancelInvoice(token, organizationId, invoiceId, reason),
		toastMessages: {
			loading: t("billing.invoices.toasts.canceling"),
			success: t("billing.invoices.toasts.cancelSuccess"),
			error: t("billing.invoices.toasts.cancelError"),
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["invoices", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["invoice", variables.organizationId, variables.invoiceId],
			});
		},
	});
};

// --- Payment Mutations ---

export const useCreateOfflinePayment = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			data,
		}: {
			token: string;
			organizationId: string;
			data: API_CreateOfflinePaymentRequest;
		}) => createOfflinePayment(token, organizationId, data),
		toastMessages: {
			loading: t("billing.payments.toasts.creating"),
			success: t("billing.payments.toasts.createSuccess"),
			error: t("billing.payments.toasts.createError"),
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["payments", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["invoices", variables.organizationId],
			});
			// Invalidate enrollment queries since payment may activate or renew enrollments
			queryClient.invalidateQueries({
				queryKey: ["organization_enrollments", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["enrollment", variables.organizationId],
			});
			if (variables.data.invoiceId) {
				queryClient.invalidateQueries({
					queryKey: [
						"invoice",
						variables.organizationId,
						variables.data.invoiceId,
					],
				});
			}
			if (variables.data.customerId) {
				queryClient.invalidateQueries({
					queryKey: [
						"customer_payments",
						variables.organizationId,
						variables.data.customerId,
					],
				});
				queryClient.invalidateQueries({
					queryKey: [
						"customer_invoices",
						variables.organizationId,
						variables.data.customerId,
					],
				});
				queryClient.invalidateQueries({
					queryKey: [
						"customer_enrollments",
						variables.organizationId,
						variables.data.customerId,
					],
				});
			}
			// Invalidate schedule session queries since payment may activate enrollments
			// which creates attendance records affecting the calendar display
			queryClient.invalidateQueries({
				queryKey: ["scheduleSessions", "monthly", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["scheduleSessions", "list", variables.organizationId],
			});
		},
	});
};

export const useCancelPayment = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			paymentId,
			reason,
		}: {
			token: string;
			organizationId: string;
			paymentId: string;
			reason?: string;
		}) => cancelPayment(token, organizationId, paymentId, reason),
		toastMessages: {
			loading: t("billing.payments.toasts.canceling"),
			success: t("billing.payments.toasts.cancelSuccess"),
			error: t("billing.payments.toasts.cancelError"),
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["payments", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["payment", variables.organizationId, variables.paymentId],
			});
			queryClient.invalidateQueries({
				queryKey: ["invoices", variables.organizationId],
			});
			// Invalidate schedule session queries since canceling payment may affect
			// enrollment status and attendance records
			queryClient.invalidateQueries({
				queryKey: ["scheduleSessions", "monthly", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["scheduleSessions", "list", variables.organizationId],
			});
		},
	});
};

// --- Refund Mutations ---

export const useCreateRefund = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			paymentId,
			data,
		}: {
			token: string;
			organizationId: string;
			paymentId: string;
			data: Omit<API_CreateRefundRequest, "paymentId">;
		}) => createRefund(token, organizationId, paymentId, data),
		toastMessages: {
			loading: t("billing.refunds.toasts.creating"),
			success: t("billing.refunds.toasts.createSuccess"),
			error: t("billing.refunds.toasts.createError"),
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["payments", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["payment", variables.organizationId, variables.paymentId],
			});
			queryClient.invalidateQueries({
				queryKey: ["invoices", variables.organizationId],
			});
			// Invalidate schedule session queries since refunds may affect
			// enrollment status and attendance records
			queryClient.invalidateQueries({
				queryKey: ["scheduleSessions", "monthly", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["scheduleSessions", "list", variables.organizationId],
			});
		},
	});
};
